import axios, { AxiosResponse } from "axios";
import { API_URL } from "../constants";
import { checkSessionExpired } from "./Auth";

export interface UploadUrlResponse {
  success: boolean;
  data: {
    documentId: string;
    filename: string;
    fileUploadUrl: string;
    metadataUploadUrl: string;
    metadataContent: {
      metadataAttributes: {
        user_id: string;
        access_level: number;
      };
    };
    s3Key: string;
    metadataS3Key: string;
  };
}

export interface IngestResponse {
  success: boolean;
  data: {
    documentId: string;
    ingestionJobId: string;
    status: string;
  };
}

export interface PendingDocument {
  userId: string;
  documentId: string;
  filename: string;
  accessLevel: number;
  uploadedAt: string;
  status: string;
}

export interface PendingDocumentsResponse {
  success: boolean;
  data: {
    totalCount: number;
    documents: PendingDocument[];
    documentsByUser: Record<string, PendingDocument[]>;
  };
}

export async function createUploadUrl(
  filename: string,
  accessLevel: number,
  accessToken: string
): Promise<UploadUrlResponse | undefined> {
  if (checkSessionExpired(accessToken)) {
    return undefined;
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const response: AxiosResponse<UploadUrlResponse> = await axios.post(
      `${API_URL}documents/upload-url`,
      { filename, accessLevel },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating upload URL:", error);
    throw error;
  }
}

export async function startDocumentIngestion(
  documentId: string,
  accessToken: string
): Promise<IngestResponse | undefined> {
  if (checkSessionExpired(accessToken)) {
    return undefined;
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const response: AxiosResponse<IngestResponse> = await axios.post(
      `${API_URL}documents/ingest`,
      { documentId },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error starting document ingestion:", error);
    throw error;
  }
}

export async function getPendingDocuments(
  accessToken: string
): Promise<PendingDocumentsResponse | undefined> {
  if (checkSessionExpired(accessToken)) {
    return undefined;
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  try {
    const response: AxiosResponse<PendingDocumentsResponse> = await axios.get(
      `${API_URL}admin/documents/pending`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching pending documents:", error);
    throw error;
  }
}

export async function approveDocument(
  documentId: string,
  accessToken: string
): Promise<any> {
  if (checkSessionExpired(accessToken)) {
    return undefined;
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const response: AxiosResponse = await axios.post(
      `${API_URL}admin/documents/${documentId}/approve`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error approving document:", error);
    throw error;
  }
}

export async function rejectDocument(
  documentId: string,
  accessToken: string
): Promise<any> {
  if (checkSessionExpired(accessToken)) {
    return undefined;
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const response: AxiosResponse = await axios.post(
      `${API_URL}admin/documents/${documentId}/reject`,
      {},
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error rejecting document:", error);
    throw error;
  }
}

export async function rejectDocumentsBatch(
  documentIds: string[],
  accessToken: string
): Promise<any> {
  if (checkSessionExpired(accessToken)) {
    return undefined;
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  try {
    const response: AxiosResponse = await axios.post(
      `${API_URL}admin/documents/reject-batch`,
      { documentIds },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error rejecting documents batch:", error);
    throw error;
  }
}

export async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  contentType: string = "application/octet-stream"
): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": contentType,
    },
  });
}

export async function uploadMetadataToS3(
  uploadUrl: string,
  metadata: object
): Promise<void> {
  await axios.put(uploadUrl, JSON.stringify(metadata), {
    headers: {
      "Content-Type": "application/json",
    },
  });
}
